<template>
  <div class="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
    <div class="w-full max-w-md dashboard-panel !bg-slate-900 p-6 relative">
      <button
        @click="emit('close')"
        class="absolute top-4 right-4 text-slate-400 hover:text-white"
        aria-label="Close"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <h2 class="text-xl font-bold text-white mb-2">Reset Password</h2>

      <form v-if="!emailSent" @submit.prevent="handleRequest" class="space-y-4">
        <p class="text-gray-200 text-sm mb-2">
          Enter your email address. If an account exists, a reset token will be sent to your inbox.
        </p>
        <div>
          <label for="reset-email" class="block text-sm font-medium text-white mb-1">Email address</label>
          <input
            id="reset-email"
            v-model="email"
            type="email"
            required
            class="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="you@example.com"
          />
        </div>

        <div v-if="error" class="bg-red-500/10 border border-red-500/30 text-red-100 rounded-lg p-3 text-sm font-medium">
          {{ error }}
        </div>

        <button
          type="submit"
          :disabled="loading"
          class="w-full h-10 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <svg v-if="loading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
          {{ loading ? 'Sending...' : 'Send Reset Token' }}
        </button>
      </form>

      <form v-else @submit.prevent="handleReset" class="space-y-4">
        <div class="bg-green-500/10 border border-green-500/30 text-green-100 rounded-lg p-3 text-sm font-medium">
          If the email exists, a reset token has been sent. Paste it below to set a new password.
        </div>

        <div>
          <label for="reset-token" class="block text-sm font-medium text-white mb-1">Reset Token</label>
          <input
            id="reset-token"
            v-model="token"
            type="text"
            required
            class="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Paste token from email"
          />
        </div>

        <div>
          <label for="new-password" class="block text-sm font-medium text-white mb-1">New Password</label>
          <input
            id="new-password"
            v-model="newPassword"
            type="password"
            required
            minlength="6"
            class="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="••••••••"
          />
        </div>

        <div v-if="error" class="bg-red-500/10 border border-red-500/30 text-red-100 rounded-lg p-3 text-sm font-medium">
          {{ error }}
        </div>
        <div v-if="success" class="bg-green-500/10 border border-green-500/30 text-green-100 rounded-lg p-3 text-sm font-medium">
          {{ success }}
        </div>

        <button
          type="submit"
          :disabled="loading || !!success"
          class="w-full h-10 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {{ loading ? 'Resetting...' : 'Set New Password' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useAuth } from '../composables/useAuth.js'

const emit = defineEmits(['close'])

const email = ref('')
const token = ref('')
const newPassword = ref('')
const success = ref('')
const emailSent = ref(false)

const { requestPasswordReset, resetPassword, loading, error } = useAuth()

const handleRequest = async () => {
  error.value = null
  success.value = ''
  try {
    await requestPasswordReset(email.value)
    emailSent.value = true
  } catch {
    // error is already set by the composable
  }
}

const handleReset = async () => {
  error.value = null
  success.value = ''
  try {
    await resetPassword(email.value, token.value, newPassword.value)
    success.value = 'Password reset successfully. You can now sign in.'
    setTimeout(() => emit('close'), 2000)
  } catch {
    // error is already set by the composable
  }
}
</script>
